// Setup › Metrics & targets at `/c/{slug}/setup/metrics` (design spec §9; `kpi`, managers).
// With the add-on off the page says "not enabled" before any query (no `kpi` call). The view
// (`?view=`) and the seat of the weights view (`?seat=`) are in the URL.
import { createRoute, useNavigate } from "@tanstack/react-router";
import {
  METRICS_VIEWS,
  MetricsScreen,
  type MetricsView,
} from "../features/structure/metrics-screen";
import { useCompany, useFeature } from "../features/use-feature";
import { validateFocusSearch } from "../focus/focus";
import { NotEnabledPage } from "../pages/status-pages";
import { setupRoute } from "./c.$slug.setup";

const ID = /^[0-9a-f-]{36}$/i;

export const setupMetricsRoute = createRoute({
  getParentRoute: () => setupRoute,
  path: "metrics",
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFocusSearch(search),
    ...((METRICS_VIEWS as readonly unknown[]).includes(search.view) && search.view !== "metrics"
      ? { view: search.view as MetricsView }
      : {}),
    ...(typeof search.seat === "string" && ID.test(search.seat) ? { seat: search.seat } : {}),
  }),
  component: function Metrics() {
    const { api } = setupMetricsRoute.useRouteContext();
    const { view, seat } = setupMetricsRoute.useSearch();
    const company = useCompany();
    const kpi = useFeature("kpi");
    const navigate = useNavigate();
    const patch = (next: { view?: MetricsView; seat?: string | undefined }) =>
      void navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => {
          const { view: v, seat: s, ...rest } = prev;
          const nextView = "view" in next ? next.view : (v as MetricsView | undefined);
          const nextSeat = "seat" in next ? next.seat : (s as string | undefined);
          return {
            ...rest,
            ...(nextView && nextView !== "metrics" ? { view: nextView } : {}),
            ...(nextSeat && nextView === "weights" ? { seat: nextSeat } : {}),
          };
        },
      } as never);
    if (!kpi.enabled)
      return (
        <NotEnabledPage
          what="Metrics and targets"
          feature="kpi"
          companyName={company.name}
          slug={company.slug}
        />
      );
    return (
      <MetricsScreen
        api={api}
        company={company}
        view={view ?? "metrics"}
        seatId={seat}
        onView={(v) => patch({ view: v })}
        onSeat={(id) => patch({ seat: id })}
      />
    );
  },
});
